/**
 * FirstRunCallout — quiet first-run context card on the empty-library surface.
 *
 * Plan: .plan/features/2026-06-14-firstrun-onboarding.md
 * Mockup: tasks/firstrun-onboarding/mockup.html
 *
 * Sits ABOVE the empty-library message + "Add recipe" CTA on first run only.
 * It tells a brand-new user the two load-bearing ideas — a recipe is a living
 * document you version as you iterate, and `@`-annotations compute — then links
 * to the syntax reference (/app/help). The wizard is wishlisted
 * (.plan/wishlist/onboarding-wizard.md); this card is the load-bearing
 * new-user landing context that the empty state must carry.
 *
 * Design refs (design.md §2):
 *   - Hairline (1px --line) + --surface fill, NO shadow — the "Add recipe" CTA
 *     below owns the one permitted accent fill + micro-shadow. This card must
 *     not compete with a second filled accent.
 *   - Accent is used ONLY as link TEXT colour (a reserved, signal use), never a
 *     second fill.
 *   - Inter (var(--font-sans)) for prose; Geist Mono (var(--font-mono)) ONLY for
 *     the literal `@` token (data). Shell sans locked to Inter per design.md §4.1,
 *     2026-05-10 Feel B register lock.
 *   - 200ms ease on hover/focus state changes; no entrance animation.
 *   - Warm neutrals via tokens; no decoration.
 *   - Rule 7: each footer control has a ≥44px tap target (minHeight:44px on a
 *     flex container with alignItems:center) so the 13px visual text does not
 *     shift layout while the hit area is large enough for mobile.
 *
 * Voice (branding.md §5 / §5.2.1):
 *   No banned words (branch, commit, fork, hash, merge, Let's, Welcome, Magic,
 *   Seamless, Effortless, Smart, Intelligent, our, Journey), no exclamations,
 *   no emoji. Uses canonical vocabulary (§8): version, variations, go-to.
 *
 * Rule 7: max-width 360px, full-width below; footer wraps; no fixed width that
 * overflows at 375px.
 */

// ── Styles ────────────────────────────────────────────────────────────────────

const calloutStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "360px",
  textAlign: "left",
  background: "var(--surface)",
  // Hairline only — design.md §2: 1px borders for structure, no shadow here.
  border: "1px solid var(--line)",
  borderRadius: "var(--radius-card, 12px)",
  padding: "var(--space-5, 20px)",
  fontFamily: "var(--font-sans)",
};

const headlineStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 600,
  color: "var(--text)",
  lineHeight: 1.4,
  margin: "0 0 var(--space-2, 8px)",
};

const bodyStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--muted)",
  lineHeight: 1.6,
  margin: "0 0 6px",
};

const bodyLastStyle: React.CSSProperties = {
  ...bodyStyle,
  marginBottom: "14px",
};

// Mono ONLY for the literal `@` token — data-only rule (design.md §2).
const atTokenStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "12px",
  color: "var(--text)",
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  // No vertical gap between wrapped rows — each item is 44px tall so wrapping
  // naturally gives enough vertical space without an additional row gap.
  gap: "0 4px",
};

// Accent as link TEXT colour — a reserved signal use, not a second fill.
// Rule 7: minHeight 44px tap target; display flex + alignItems center keeps the
// 13px visual text centred without adding whitespace above/below to the layout.
const linkStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: "44px",
  paddingRight: "12px",
  fontSize: "13px",
  fontWeight: 500,
  color: "var(--accent)",
  textDecoration: "none",
  cursor: "pointer",
  transition: "opacity 200ms ease",
  WebkitTapHighlightColor: "transparent",
};

const dismissStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: "44px",
  paddingLeft: "12px",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  color: "var(--faint)",
  background: "none",
  border: "none",
  // Left border acts as a visual separator so the two controls cannot be
  // straddled by a single finger tap at 375px.
  borderLeft: "1px solid var(--line)",
  cursor: "pointer",
  transition: "color 200ms ease",
  WebkitTapHighlightColor: "transparent",
};

const CALLOUT_CSS = `
  .proof-firstrun-link:hover {
    opacity: 0.82;
  }
  .proof-firstrun-link:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 4px;
  }
  .proof-firstrun-dismiss:hover {
    color: var(--text);
  }
  .proof-firstrun-dismiss:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

if (typeof document !== "undefined") {
  const id = "proof-firstrun-callout-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = CALLOUT_CSS;
    document.head.appendChild(el);
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface FirstRunCalloutProps {
  /** Dismisses the callout (sets the persisted flag in the parent hook). */
  onDismiss: () => void;
  /**
   * Navigates to the syntax-reference page (/app/help). Optional so the
   * component renders standalone in tests without a router; when absent the
   * link falls back to a plain href.
   */
  onOpenSyntaxReference?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FirstRunCallout({ onDismiss, onOpenSyntaxReference }: FirstRunCalloutProps) {
  function handleLinkClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (onOpenSyntaxReference) {
      // SPA navigation — prevent the full-page href fallback.
      e.preventDefault();
      onOpenSyntaxReference();
    }
    // When no handler is provided, the anchor's href="/app/help" navigates.
  }

  return (
    <div
      style={calloutStyle}
      role="note"
      aria-label="Getting started"
      data-testid="firstrun-callout"
    >
      {/*
       * a11y fix: promoted from <p> to <h2> so AT users can locate / skip the
       * callout via heading navigation. role="note" on the card div allows
       * screen-reader users to jump to or past it as a landmark.
       */}
      <h2 style={headlineStyle}>A recipe here is a living document.</h2>
      <p style={bodyStyle}>
        Save a version each time you cook, keep variations side by side, and mark one as your go-to.
      </p>
      <p style={bodyLastStyle}>
        Write <span style={atTokenStyle}>@</span>-annotations that compute — scale quantities,
        record ratings, define values.
      </p>
      <div style={footerStyle}>
        <a
          href="/app/help"
          className="proof-firstrun-link"
          style={linkStyle}
          onClick={handleLinkClick}
          data-testid="firstrun-syntax-link"
        >
          Syntax reference
        </a>
        <button
          type="button"
          className="proof-firstrun-dismiss"
          style={dismissStyle}
          onClick={onDismiss}
          data-testid="firstrun-dismiss"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
